import { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import type { City, Project } from '@/lib/types';
import type { ComboboxOption } from '@/components/ui';
import { citySubtitle, projectSeason } from './event-map-rules';

/**
 * The records an event-map key may point at — every project, or every city.
 *
 * 🔴 **IT EXISTS BECAUSE A MAPPING ROW CARRIES A `MasterRef` AND NOTHING MORE.**
 * `{id, name}` is all the wire gives, and **`Project.name` is explicitly not
 * unique** — one product genuinely runs twice in a period — so a table built
 * from the row alone prints two festivals under one word and cannot be used to
 * check a mapping at all. The season comes from the PROJECT, so the project list
 * has to be in hand wherever a mapping is displayed, not only where one is
 * chosen.
 *
 * ⚠️ **Both the form and the table use this, so a name reads the same in the
 * picker as it does in the row it produced.** Two derivations of "which season
 * is this" would agree on the day they were written and drift on the first
 * correction — on the one screen whose entire job is noticing that two similar
 * things are not the same thing.
 *
 * ⚠️ **Mounting it twice costs two reads of the same list**, and that is
 * accepted rather than overlooked: the form only mounts while it is open, and
 * the alternative is threading the records down from the page, where a stale
 * copy handed to a form would be a mapping saved against a list nobody refreshed.
 */
export type EventMapTargetKind = 'project' | 'city';

export interface EventMapTargetsState {
  /** For a picker. Built from {@link records}, in the order they arrived. */
  options: ComboboxOption[];
  /** For a table: the full record behind a mapping's `{id, name}`. */
  byId: ReadonlyMap<number, Project | City>;
  loading: boolean;
  /** The server's own sentence. `null` while a read is in flight or after one succeeds. */
  error: string | null;
  /** Non-empty only when the list really is short. */
  footnote: string;
}

/**
 * 🔴 **PROJECTS ARE READ IN FULL, PAGE BY PAGE, AND A SHORT READ THROWS.** There
 * are ~118 active projects against a `perPage` ceiling of 100, so a single-page
 * read silently drops the tail of the alphabet — and because the order is stable
 * it is **the same rows missing every time**, which reads as a naming convention
 * rather than as a truncation. That has already cost this app a picker offering
 * NOTHING for a product whose projects all sorted past the cut. `listAllProjects`
 * is the house answer and it refuses to resolve short.
 *
 * ⚠️ **Cities are ONE read at the masters' ceiling of 200, with an honest
 * footnote if that is not enough.** There is no paging loop on `/cities` in this
 * app, and building one for the 28 rows that exist would be designing for a cap
 * nobody is near. The footnote is what stops a truncated list looking complete.
 *
 * ⚠️ **RETIRED RECORDS ARE INCLUDED, LABELLED, RATHER THAN FILTERED OUT.** A
 * picker normally offers only what may be CHOSEN — but a mapping outlives a
 * season, so a live row can legitimately point at a project nobody sells any
 * more. Narrowing to active would blank the trigger on exactly those rows, the
 * next save would reassign them to whatever was picked instead, and the TABLE
 * would lose the season on the very rows most likely to be wrong.
 */
export function useEventMapTargets(kind: EventMapTargetKind): EventMapTargetsState {
  const [records, setRecords] = useState<(Project | City)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [footnote, setFootnote] = useState('');

  const load = useCallback(
    (signal: AbortSignal) => {
      setLoading(true);

      const run: Promise<(Project | City)[]> =
        kind === 'project'
          ? api.listAllProjects(signal).then((rows) => {
              setFootnote('');
              return rows;
            })
          : api.listCities({ page: 1, perPage: 200 }, signal).then((response) => {
              const rows = Array.isArray(response.data) ? response.data : [];
              const total = response.meta?.total;
              /*
               * ⚠️ Rendered ONLY when the list really is short. An unconditional
               * footnote claims a limit that is not being hit — and on this
               * primitive a footnote also suppresses controls elsewhere, so an
               * idle one is not free.
               */
              setFootnote(
                typeof total === 'number' && total > rows.length
                  ? `Showing the first ${rows.length} of ${total} cities.`
                  : '',
              );
              return rows;
            });

      return run
        .then((rows) => {
          setRecords(rows);
          setError(null);
        })
        .catch((caught: unknown) => {
          if (signal.aborted) return;
          /*
           * ⛔ The list is EMPTIED rather than left holding a stale one. A picker
           * still offering yesterday's options beside a failure message invites a
           * save against a list nobody can vouch for.
           */
          setRecords([]);
          setError(caught instanceof Error ? caught.message : 'The list could not be read.');
        })
        .finally(() => {
          if (!signal.aborted) setLoading(false);
        });
    },
    [kind],
  );

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const byId = useMemo(() => {
    const map = new Map<number, Project | City>();
    for (const record of records) map.set(record.id, record);
    return map;
  }, [records]);

  const options = useMemo<ComboboxOption[]>(
    () =>
      records.map((record) => {
        const subtitle = subtitleOf(record);
        /*
         * ⚠️ **The hint carries the discriminator and is SEARCHABLE**, which is
         * what lets somebody type "Summer 2027" and find the right festival
         * among three sharing a name. An option's `hint` labels an option and
         * has never been in scope for the no-helper-text rule.
         */
        const hintParts = [subtitle, record.isActive ? '' : 'no longer offered'].filter(Boolean);
        return {
          value: String(record.id),
          label: record.name,
          ...(hintParts.length > 0 ? { hint: hintParts.join(' · ') } : {}),
        };
      }),
    [records],
  );

  /*
   * ⚠️ **A stored id absent from the list is NOT injected back as a bare
   * option.** Everything is offered already — active and retired alike — so the
   * only way to be missing is to have been deleted, and a synthesised row
   * reading "12" would let somebody re-save a mapping onto a record that no
   * longer exists. The form says so instead.
   */

  return { options, byId, loading, error, footnote };
}

/**
 * The line under a record's name.
 *
 * ⚠️ **Narrowed on a key only a project has, never on a cast**, so a city
 * reaching the project arm is a compile error rather than a season computed from
 * fields that are not there.
 */
export function subtitleOf(record: Project | City): string {
  return 'cycle' in record ? projectSeason(record) : citySubtitle(record);
}
